/* Banking v2.0.0 shared guidance — catalog-level modules rendered ONCE per role page
 * using the existing Section / Accordion / InfoCard / Bullets primitives. */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layers, ArrowRight } from 'lucide-react';
import { Section, InfoCard, Bullets, Accordion } from '../preview-ui/PreviewPrimitives';

const humanize = (k) => String(k || '').replace(/_/g, ' ').replace(/^(\d)\s/, 'Layer $1 — ').replace(/^\w/, (c) => c.toUpperCase());

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 underline break-all">{children}</a>
);

const Src = ({ ids, reg }) => {
  const items = (ids || []).map((i) => reg[i]).filter(Boolean);
  if (!items.length) return null;
  return (
    <p className="mt-2 text-xs text-gray-600">
      Sources:{' '}
      {items.map((s, i) => (
        <span key={s.id}>{i > 0 && ' · '}<Ext href={s.url}>{s.title}</Ext></span>
      ))}
    </p>
  );
};

const Label = ({ children }) => (
  <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-teal-800 mb-2 mt-4 first:mt-0">{children}</p>
);

const Overview = ({ o, reg }) => (
  <div className="space-y-3">
    {['what_is_an_ai_agent_in_banking', 'the_core_pattern_maker_checker', 'where_agents_are_growing_fastest', 'what_stays_human_across_banking'].map((k) => o[k] && (
      <InfoCard key={k} label={humanize(k)} tone="sky">{o[k]}</InfoCard>
    ))}
    {o.skills_that_make_students_agent_ready?.length > 0 && (<><Label>Skills that make students agent-ready</Label><Bullets items={o.skills_that_make_students_agent_ready} tone="teal" /></>)}
    {o.student_warning && <InfoCard label="Student warning" tone="rose">{o.student_warning}</InfoCard>}
    {o.evidence_type && <p className="text-xs text-gray-500">{o.evidence_type}</p>}
  </div>
);

const AutomationReality = ({ a, reg }) => (
  <div className="space-y-3">
    {a.statement && <p>{a.statement}</p>}
    {a.formula && (
      <InfoCard label="Planning formula" tone="cream">
        <p className="font-semibold">{a.formula}</p>
        {a.formula_status && <p className="mt-2 text-sm">{a.formula_status}</p>}
      </InfoCard>
    )}
    {a.what_must_change?.length > 0 && (<><Label>What must change for higher automation</Label><Bullets items={a.what_must_change} tone="amber" /></>)}
    {Array.isArray(a.planning_windows) && a.planning_windows.length > 0 && (
      <>
        <Label>Planning windows — scenarios, not forecasts</Label>
        <div className="grid sm:grid-cols-3 gap-3" data-testid="bank-shared-planning-windows">
          {a.planning_windows.map((w, i) => (
            <InfoCard key={i} label={`${w.window} · ${w.status}`} tone="sky">
              <p>{w.possibility}</p>
              {w.not_implied && <p className="mt-2 text-xs text-gray-600">Not implied: {w.not_implied}</p>}
            </InfoCard>
          ))}
        </div>
      </>
    )}
    {a.agi_and_asi && (
      <InfoCard label="AGI and ASI — what is and is not claimed" tone="violet">
        {['meaning', 'strong_scenario', 'no_deadline', 'action'].map((k) => a.agi_and_asi[k] && <p key={k} className="mb-2 last:mb-0"><span className="font-semibold">{humanize(k)}:</span> {a.agi_and_asi[k]}</p>)}
      </InfoCard>
    )}
    {a.research_anchor?.text && <InfoCard label="Research anchor" tone="mint">{a.research_anchor.text}<Src ids={a.research_anchor.source_ids} reg={reg} /></InfoCard>}
  </div>
);

const ExposureMethod = ({ e }) => (
  <div className="space-y-3">
    {e.basis && <p>{e.basis}</p>}
    <div className="grid md:grid-cols-3 gap-3">
      {Object.entries(e.labels || {}).map(([k, v]) => <InfoCard key={k} label={humanize(k)} tone={k === 'higher' ? 'rose' : k === 'mixed' ? 'cream' : 'mint'}>{v}</InfoCard>)}
    </div>
    {e.catalogue_label_counts && (
      <p className="text-sm text-gray-700">
        Catalogue labels: {Object.entries(e.catalogue_label_counts).map(([k, v]) => `${humanize(k)} ${v}`).join(' · ')}. {e.count_warning}
      </p>
    )}
    {e.limitations?.length > 0 && (<><Label>Limitations</Label><Bullets items={e.limitations} tone="rose" /></>)}
  </div>
);

const Cards = ({ cards, reg, tone = 'teal', testId }) => (
  <div className="grid md:grid-cols-2 gap-3" data-testid={testId}>
    {(cards || []).map((c) => (
      <InfoCard key={c.id || c.title} label={c.title || c.name} tone={tone}>
        {c.text && <p>{c.text}</p>}
        {c.meaning && <p>{c.meaning}</p>}
        {c.practice && <p className="mt-2 text-sm"><span className="font-semibold">Practice:</span> {c.practice}</p>}
        {c.evidence_type && <p className="mt-2 text-xs text-gray-500">{humanize(c.evidence_type)}</p>}
        <Src ids={c.source_ids} reg={reg} />
      </InfoCard>
    ))}
  </div>
);

const EntryClarity = ({ e, reg }) => (
  <div className="space-y-3">
    {e.global_heading && <p className="font-bold text-gray-900">{e.global_heading}</p>}
    {e.global_copy && <p>{e.global_copy}</p>}
    <div className="grid md:grid-cols-2 gap-3">
      {['class10_class12', 'graduate_generalist', 'specialist', 'experienced'].map((k) => e[k] && <InfoCard key={k} label={humanize(k)} tone="mint">{e[k]}</InfoCard>)}
    </div>
    {e.credential_boundary && <InfoCard label="Credential boundary" tone="rose">{e.credential_boundary}</InfoCard>}
    {e.before_paying?.length > 0 && (<><Label>Before paying for coaching or courses</Label><Bullets items={e.before_paying} tone="amber" /></>)}
    {e.notice_checklist?.length > 0 && (<><Label>Recruitment notice checklist</Label><Bullets items={e.notice_checklist} tone="teal" /></>)}
    {e.directories?.length > 0 && (
      <>
        <Label>Official directories — check the exact notice</Label>
        <ul className="space-y-1 text-sm" data-testid="bank-shared-directories">
          {e.directories.map((d) => <li key={d.source_id}><Ext href={d.url}>{reg[d.source_id]?.title || d.url}</Ext></li>)}
        </ul>
        {e.directory_note && <p className="text-xs text-gray-600 mt-2">{e.directory_note}</p>}
      </>
    )}
  </div>
);

const ProviderGuide = ({ g, reg, H }) => (
  <div className="space-y-3" data-testid="bank-preview-ecosystem">
    {g.why_this_section_exists && <p>{g.why_this_section_exists}</p>}
    {g.the_four_layers_of_the_ai_stack && (
      <div className="grid md:grid-cols-2 gap-3" data-testid="bank-eco-layers">
        {Object.entries(g.the_four_layers_of_the_ai_stack).map(([k, v]) => <InfoCard key={k} label={humanize(k)} tone="sky">{v}</InfoCard>)}
      </div>
    )}
    {g.provider_profiles?.length > 0 && (
      <>
        <Label>AI provider guide — documented capability vs. banking proof</Label>
        <div className="grid md:grid-cols-2 gap-3" data-testid="bank-eco-providers">
          {g.provider_profiles.map((p) => (
            <InfoCard key={p.id} label={p.name} tone="violet">
              {p.what_is_documented && <p>{p.what_is_documented}</p>}
              {p.limits && <p className="mt-2 text-sm"><span className="font-semibold">Limits:</span> {p.limits}</p>}
              {p.learn && <p className="mt-2 text-sm"><span className="font-semibold">Learn:</span> {p.learn}</p>}
              <Src ids={p.source_ids} reg={reg} />
            </InfoCard>
          ))}
        </div>
      </>
    )}
    {g.verified_real_deployments_2025_26?.length > 0 && (
      <>
        <Label>{H.verified_real_deployments_2025_26 || 'Reported banking examples'}</Label>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="bank-eco-deployments">
          {g.verified_real_deployments_2025_26.map((d, i) => (
            <InfoCard key={i} label={d.organisation || `${d.bank} × ${d.vendor}`} tone="mint">
              {d.evidence_status && <p className="text-xs font-semibold text-emerald-800 mb-1">{d.evidence_status}</p>}
              <p>{d.fact}</p>
              <Src ids={d.source_ids} reg={reg} />
            </InfoCard>
          ))}
        </div>
      </>
    )}
    {g.india_regulatory_anchor && (
      <InfoCard label={g.india_regulatory_anchor.name || 'India regulatory anchor'} tone="cream">
        <div data-testid="bank-eco-rbi">
          {g.india_regulatory_anchor.fact && <p>{g.india_regulatory_anchor.fact}</p>}
          {g.india_regulatory_anchor.why_students_should_care && <p className="mt-2">{g.india_regulatory_anchor.why_students_should_care}</p>}
          {g.india_regulatory_anchor.source && <p className="mt-2 text-sm"><Ext href={g.india_regulatory_anchor.source}>{g.india_regulatory_anchor.source}</Ext></p>}
        </div>
      </InfoCard>
    )}
    {g.free_learning_paths?.length > 0 && (
      <>
        <Label>{H.free_learning_paths || 'Provider resources'}</Label>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="bank-eco-learning">
          {g.free_learning_paths.map((lp, i) => (
            <InfoCard key={i} label={lp.provider} tone="teal">
              {lp.what}
              {lp.url && <p className="mt-2 text-sm"><Ext href={lp.url}>{lp.url}</Ext></p>}
            </InfoCard>
          ))}
        </div>
        {g.learning_cost_note && <p className="text-xs text-gray-600 mt-2">{g.learning_cost_note}</p>}
      </>
    )}
    {g.interview_power_move && <InfoCard label="Interview power move" tone="violet"><span className="italic">{g.interview_power_move}</span></InfoCard>}
  </div>
);

const Emerging = ({ e, reg }) => (
  <div className="space-y-3">
    {e.note && <p>{e.note}</p>}
    <div className="grid md:grid-cols-2 gap-3" data-testid="bank-shared-emerging">
      {(e.cards || []).map((c) => (
        <InfoCard key={c.id} label={c.name} tone="sky">
          {c.status && <p className="text-xs font-semibold text-sky-800 mb-1">{c.status}</p>}
          {c.work && <p>{c.work}</p>}
          {c.preparation && <p className="mt-2 text-sm"><span className="font-semibold">Preparation:</span> {c.preparation}</p>}
          {c.connects_to?.length > 0 && <p className="mt-2 text-xs text-gray-600">Connects to catalogue roles #{c.connects_to.join(', #')}</p>}
          <Src ids={c.source_ids} reg={reg} />
        </InfoCard>
      ))}
    </div>
    {e.future_limit && <p className="text-sm text-gray-700">{e.future_limit}</p>}
  </div>
);

const Routes = ({ r }) => (
  <div className="space-y-3">
    {r.text && <p>{r.text}</p>}
    <div className="flex flex-wrap gap-2" data-testid="bank-shared-routes">
      {(r.links || []).map((l) => l.to ? (
        <Link key={l.target} to={l.to} data-testid={`bank-route-${l.target}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors">
          {l.label} <ArrowRight size={14} />
        </Link>
      ) : (
        <span key={l.target} className="text-sm text-gray-500">{l.label} — destination not available yet</span>
      ))}
    </div>
  </div>
);

const Sources = ({ list }) => (
  <ol className="space-y-2 text-sm" data-testid="bank-shared-sources">
    {(list || []).map((s, i) => (
      <li key={s.id} className="flex gap-2">
        <span className="text-gray-400 shrink-0">{i + 1}.</span>
        <span>
          <Ext href={s.url}>{s.title}</Ext>
          {s.publisher && <span className="text-gray-600"> — {s.publisher}</span>}
          {s.reviewed_date && <span className="text-gray-500"> · reviewed {s.reviewed_date}</span>}
          {s.use_limit && <span className="block text-xs text-gray-500 mt-0.5">{s.use_limit}</span>}
        </span>
      </li>
    ))}
  </ol>
);

const Review = ({ v, rn, legacyDate }) => (
  <div className="space-y-3" data-testid="bank-shared-review">
    <InfoCard label="Content review date — not an eligibility check" tone="cream">
      <p>Content reviewed {v.reviewed_on || legacyDate}. {v.standing_warning}</p>
      {v.method && <p className="mt-2 text-sm"><span className="font-semibold">Method:</span> {v.method}</p>}
      {v.scope_limit && <p className="mt-2 text-sm"><span className="font-semibold">Scope limit:</span> {v.scope_limit}</p>}
    </InfoCard>
    {v.corrections_made?.length > 0 && (<><Label>Corrections made in this edition</Label><Bullets items={v.corrections_made} tone="teal" /></>)}
    {v.not_verified?.length > 0 && (<><Label>Not verified</Label><Bullets items={v.not_verified} tone="rose" /></>)}
    {rn && (
      <InfoCard label={`Change notes · version ${rn.version || ''} · ${rn.date || ''}`} tone="sky">
        {rn.previous && <p><span className="font-semibold">Previous:</span> {rn.previous}</p>}
        {rn.updated && <p className="mt-2"><span className="font-semibold">Updated:</span> {rn.updated}</p>}
        {rn.existing_user_message && <p className="mt-2 text-sm italic">{rn.existing_user_message}</p>}
      </InfoCard>
    )}
  </div>
);

export default function BankingSharedGuidance({ shared }) {
  const reg = useMemo(() => Object.fromEntries((shared?.source_register || []).map((s) => [s.id, s])), [shared]);
  if (!shared || !Object.keys(shared).length) return null;
  const H = shared.heading_overrides || {};
  const g = shared.ai_ecosystem_guide || {};
  const modules = [
    shared.ai_agent_integration_overview && { id: 'overview', title: 'AI agents in banking — overview', body: <Overview o={shared.ai_agent_integration_overview} reg={reg} />, open: true },
    shared.automation_reality && { id: 'automation-reality', title: shared.automation_reality.heading, body: <AutomationReality a={shared.automation_reality} reg={reg} /> },
    shared.exposure_method && { id: 'exposure', title: shared.exposure_method.heading, body: <ExposureMethod e={shared.exposure_method} /> },
    shared.india_banking_controls && { id: 'controls', title: shared.india_banking_controls.heading, body: (
      <div className="space-y-3">
        {shared.india_banking_controls.scope && <p>{shared.india_banking_controls.scope}</p>}
        <Cards cards={shared.india_banking_controls.cards} reg={reg} tone="cream" testId="bank-shared-controls" />
        {shared.india_banking_controls.learner_practice_rule && <InfoCard label="Learner practice rule" tone="rose">{shared.india_banking_controls.learner_practice_rule}</InfoCard>}
      </div>
    ) },
    shared.entry_and_training_clarity && { id: 'entry', title: shared.entry_and_training_clarity.heading, body: <EntryClarity e={shared.entry_and_training_clarity} reg={reg} /> },
    Object.keys(g).length > 0 && { id: 'providers', title: 'AI provider guide and reported banking examples', body: <ProviderGuide g={g} reg={reg} H={H} /> },
    shared.banking_foundations && { id: 'foundations', title: shared.banking_foundations.heading, body: (
      <div className="space-y-3">
        {shared.banking_foundations.scope && <p>{shared.banking_foundations.scope}</p>}
        <Cards cards={shared.banking_foundations.cards} reg={reg} tone="mint" testId="bank-shared-foundations" />
        {shared.banking_foundations.role_connection && <p className="text-sm text-gray-700">{shared.banking_foundations.role_connection}</p>}
      </div>
    ) },
    Array.isArray(shared.prepare_by_stage) && { id: 'prepare', title: 'Prepare by stage', body: (
      <div className="grid md:grid-cols-3 gap-3" data-testid="bank-shared-prepare">
        {shared.prepare_by_stage.map((p) => <InfoCard key={p.stage} label={p.stage} tone="teal">{p.guidance}</InfoCard>)}
      </div>
    ) },
    shared.emerging_work_directions && { id: 'emerging', title: shared.emerging_work_directions.heading, body: <Emerging e={shared.emerging_work_directions} reg={reg} /> },
    shared.alternative_earning_routes && { id: 'routes', title: shared.alternative_earning_routes.heading, body: <Routes r={shared.alternative_earning_routes} /> },
    shared.source_register && { id: 'sources', title: `Sources (${shared.source_register.length})`, body: <Sources list={shared.source_register} /> },
    shared.verification_summary && { id: 'review', title: H.verification || 'Content Review and Sources', body: <Review v={shared.verification_summary} rn={shared.release_notes} legacyDate={shared.legacy_review_date} /> },
  ].filter(Boolean);

  return (
    <Section id="ai-ecosystem" icon={Layers} title="Shared AI Guidance for Banking" accent="teal">
      <div className="space-y-3" data-testid="bank-preview-shared-guidance">
        <p className="text-sm text-gray-600">
          Shared across all banking roles. Current capabilities, reported deployments and predictions are labelled separately — read the date and scope on each item.
        </p>
        {modules.map((m) => (
          <Accordion key={m.id} title={m.title} defaultOpen={!!m.open} testId={`bank-shared-${m.id}`}>
            {m.body}
          </Accordion>
        ))}
      </div>
    </Section>
  );
}
